import clsx from 'clsx';
import type { HTMLAttributes, ReactNode } from 'react';

/**
 * toncenter.com hairline frame (DESIGN.md §4).
 *
 * A `Frame` is one full-width band of the page. Inside it a centred box
 * (max 1496px, `--frame-pad` gutters) carries 1px rails on both edges.
 * `lines` draws full-viewport hairlines on the band's top and/or bottom
 * edge, and every place such a hairline meets a rail gets a 14px
 * four-point-star junction — the signature of the toncenter.com grid.
 * `splits` adds vertical column dividers (with T-junctions) inside the box;
 * they hide below 992px, where columns stack.
 *
 * Consecutive bands share an edge: give the hairline to ONE of them
 * (usually the upper band's `bottom`) so the line is never drawn twice.
 */
export type FrameLines = 'none' | 'top' | 'bottom' | 'both';

type FrameTag = 'div' | 'section' | 'header' | 'footer';

interface FrameProps extends HTMLAttributes<HTMLElement> {
  as?: FrameTag;
  lines?: FrameLines;
  /** Column divider positions, in percent of the frame box width. */
  splits?: readonly number[];
  /** Classes for the full-width band (e.g. `sticky top-0`). */
  bandClassName?: string;
  /** Classes for the content box between the rails. */
  className?: string;
  children?: ReactNode;
}

// toncenter.com junction glyphs, verbatim.
const STAR_PATH =
  'M13.1474 6.3275C10.3613 5.83874 8.16126 3.6387 7.6725 0.852633L7.5 0H6.5L6.3275 0.852633C5.83874 3.6387 3.6387 5.83874 0.852634 6.3275L0 6.5V7.5L0.852634 7.6725C3.6387 8.16126 5.83874 10.3613 6.3275 13.1474L6.5 14H7.5L7.6725 13.1474C8.16126 10.3613 10.3613 8.16126 13.1474 7.6725L14 7.5V6.5L13.1474 6.3275Z';
const TEE_DOWN_PATH =
  'M14 6.5V7.5L13.1474 7.6725C10.3613 8.16126 8.16126 10.3613 7.6725 13.1474L7.5 14H6.5L6.3275 13.1474C5.83874 10.3613 3.6387 8.16126 0.852634 7.6725L0 7.5V6.5H14Z';
const TEE_UP_PATH =
  'M0 6.5L0.852634 6.3275C3.6387 5.83874 5.83874 3.6387 6.3275 0.852633L6.5 0H7.5L7.6725 0.852633C8.16126 3.6387 10.3613 5.83874 13.1474 6.3275L14 6.5V7.5H0V6.5Z';

function Junction({ path, className }: { path: string; className: string }) {
  return (
    <svg
      aria-hidden
      focusable="false"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      className={clsx('tc-junction', className)}
    >
      <path d={path} fill="currentColor" />
    </svg>
  );
}

export function Frame({
  as: Tag = 'div',
  lines = 'none',
  splits = [],
  bandClassName,
  className,
  children,
  ...rest
}: FrameProps) {
  const top = lines === 'top' || lines === 'both';
  const bottom = lines === 'bottom' || lines === 'both';
  return (
    <Tag {...rest} className={clsx('tc-frame-band', bandClassName)}>
      {top && <span aria-hidden className="tc-hline tc-hline-top" />}
      {bottom && <span aria-hidden className="tc-hline tc-hline-bottom" />}
      <div className="tc-frame-box">
        <span aria-hidden className="tc-rail tc-rail-left" />
        <span aria-hidden className="tc-rail tc-rail-right" />
        {/* No z-index here on purpose: a stacking context would trap the
            modals and popovers rendered inside (z-40/50) under the sticky
            header and the next band. Rails/lines sit above via z-2/3. */}
        <div className={clsx('relative w-full min-w-0', className)}>
          {children}
        </div>
        {top && (
          <>
            <Junction
              path={STAR_PATH}
              className="tc-junction-top tc-junction-start"
            />
            <Junction
              path={STAR_PATH}
              className="tc-junction-top tc-junction-end"
            />
          </>
        )}
        {bottom && (
          <>
            <Junction
              path={STAR_PATH}
              className="tc-junction-bottom tc-junction-start"
            />
            <Junction
              path={STAR_PATH}
              className="tc-junction-bottom tc-junction-end"
            />
          </>
        )}
        {splits.map((pos) => (
          <span
            key={pos}
            aria-hidden
            className="tc-split"
            style={{ left: `${String(pos)}%` }}
          >
            <Junction
              path={TEE_DOWN_PATH}
              className="tc-junction-top tc-junction-split"
            />
            <Junction
              path={TEE_UP_PATH}
              className="tc-junction-bottom tc-junction-split"
            />
          </span>
        ))}
      </div>
    </Tag>
  );
}

/**
 * Horizontal hairline across the frame box with half-star junctions on the
 * rails — toncenter.com's divider between columns that have stacked (the
 * `splits` hide below 992px). Place it as the first child of a
 * `relative` column; pass `className` to hide it where columns sit side by
 * side.
 */
export function FrameDivider({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={clsx(
        'pointer-events-none absolute inset-x-0 top-0 z-[2] block h-0',
        className,
      )}
    >
      <span className="absolute inset-x-0 top-0 h-px bg-line" />
      <Junction
        path={STAR_PATH}
        className="tc-junction-top tc-junction-start [clip-path:inset(0_0_0_50%)]"
      />
      <Junction
        path={STAR_PATH}
        className="tc-junction-top tc-junction-end [clip-path:inset(0_50%_0_0)]"
      />
    </span>
  );
}
